<script>
import MottoIcon from '@components/motto-icon.vue'
import FavoriteHeart from '@components/favorite-heart.vue'
import LoaderIcon from '@components/loader-icon.vue'
import CopyToClipboardButton from '@components/copy-to-clipboard-button.vue'
import ResourceBlock from '@components/resources/resource-block.vue'
import { addFavoriteEvent, deleteFavoriteEvent, getEventById } from '@src/api/events'
import { transform as transformEvent } from '@src/api/transformers/eventTransformer'
import { transform as transformResource } from '@src/api/transformers/resourceTransformer'
import { getDateTimeTexts } from '@utils/date-helpers'
import { eventsComputed } from '@state/helpers'
import moment from 'moment'
import { addEventToCalendarUrl } from '@utils/add-event-to-calendar'
import { arrayToSentence } from '@utils/string-helpers'

export default {
  components: {
    MottoIcon,
    FavoriteHeart,
    LoaderIcon,
    ResourceBlock,
    CopyToClipboardButton,
  },
  props: {
    toastComponent: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      loading: true,
      loadingFavorite: false,
      error: false,
      event: {},
      eventId: null,
      modalIdentifier: 'view-event-modal',
      bvToast: this.toastComponent || this.$bvToast,
    }
  },
  computed: {
    ...eventsComputed,
    getEventPosts() {
      return this.event?.posts?.map(transformResource) || []
    },
    getStartTimeTexts() {
      return getDateTimeTexts(moment(this.event.start, 'YYYY-MM-DD hh:mm:ss').toDate())
    },
    getEndTimeTexts() {
      return getDateTimeTexts(moment(this.event.end, 'YYYY-MM-DD hh:mm:ss').toDate())
    },

    isFavorite() {
      return this.isEventFavorite(this.eventId)
    },

    eventUrl() {
      return window.location.origin + '/event/' + this.eventId
    },

    getOutlookUrl() {
      return addEventToCalendarUrl(this.event)
    },

    getAudienceSentence() {
      return arrayToSentence(this.event.audience || [])
    },
  },
  created() {
    this.eventId = this.$store.getters['events/selectedEventId']
    this.fetchEvent(this.eventId)
  },
  methods: {
    async fetchEvent(eventId) {
      try {
        const { data: event } = await getEventById({ eventId })
        this.event = transformEvent(event)
        this.syncFavoritePosts(this.event.posts)
      } catch (err) {
        this.bvToast.toast('There was an error loading this event. Please try again later', {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
        this.closeModal()
      } finally {
        this.loading = false
      }
    },

    async handleHeartClick() {
      if (this.loadingFavorite) {
        return
      }

      this.loadingFavorite = true
      if (this.isFavorite) {
        await this.unfavoriteEvent()
      } else {
        await this.favoriteEvent()
      }
      this.loadingFavorite = false
    },

    async favoriteEvent() {
      await addFavoriteEvent({ eventId: this.event.eventId })
      this.$store.commit('events/FAVORITE_EVENT_BY_ID', this.event.eventId)
    },

    async unfavoriteEvent() {
      await deleteFavoriteEvent({ eventId: this.event.eventId })
      this.$store.commit('events/UNFAVORITE_EVENT_BY_ID', this.event.eventId)
    },

    closeModal() {
      this.$bvModal.hide(this.modalIdentifier)
    },

    openUrl(url) {
      window.open(url, '_blank')
    },

    openEventUrl({ url, urlType }) {
      if (this.event.hasElapsed && urlType === 'registration_link') {
        this.bvToast.toast('This Event Registration has expired', {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-warning',
        })
        return
      }

      this.openUrl(url)
    },

    syncFavoritePosts(posts) {
      if (!posts) {
        return
      }

      const favoritePosts = posts.filter((resource) => resource.isFavorite)
      favoritePosts.forEach(({ id: resourceId }) =>
        this.$store.commit('resources/ADD_RESOURCE_ID_TO_FAVORITE_IDS', resourceId)
      )
    },
  },
}
</script>

<template>
  <div id="modal-event-container" :class="$style.container" data-modal-event>
    <LoaderIcon v-if="loading || error" :error="error" class="m-auto"></LoaderIcon>
    <div v-else :class="$style.modalEventContainer">
      <MottoIcon
        :class="$style.closeButton"
        name="X"
        icon-color="gray"
        data-close-modal-event-button
        @click.native="closeModal" />
      <div :class="$style.contentContainer">
        <div :class="$style.contentHeader">
          <div :class="$style.breadcrumbs">
            <p class="p2 color-darkest-gray cursor-pointer">EVENTS</p>
          </div>
        </div>
        <div class="position-relative">
          <BaseLink
            :class="$style.eventTitleLink"
            :to="{
              name: 'event',
              params: {
                eventId,
              },
            }">
            <h2 :class="$style.eventTitle" class="color-primary-blue">{{ event.title }}</h2>
          </BaseLink>

          <LoaderIcon v-if="loadingFavorite" class="d-inline" :class="$style.heartIcon" />
          <FavoriteHeart
            v-else-if="event.eventId"
            :class="$style.heartIcon"
            :is-favorite="isFavorite"
            @handleHeartClick="handleHeartClick" />
          <CopyToClipboardButton :text-to-copy="eventUrl" :class="$style.clipboardButton" />
        </div>
        <div :class="$style.subTitleContent">
          <p v-if="!event.multiDay" class="p1 color-primary-blue" :class="$style.subTitleEventDate">
            {{ getStartTimeTexts.weekdayLong }}, {{ getStartTimeTexts.month }} {{ getStartTimeTexts.day }}
          </p>
          <p v-if="event.multiDay" class="p1 color-primary-blue" :class="$style.subTitleEventDate">
            {{ getStartTimeTexts.weekday }}, {{ getStartTimeTexts.monthShort }} {{ getStartTimeTexts.day }} -
            {{ getEndTimeTexts.weekday }}, {{ getEndTimeTexts.monthShort }} {{ getEndTimeTexts.day }}
          </p>
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <MottoIcon name="Clock" icon-color="gray" class="mr-10"></MottoIcon>
              <p class="p1 color-primary-blue">
                {{ event.allDay ? `ALL DAY` : `${getStartTimeTexts.time} - ${getEndTimeTexts.time}` }}
              </p>
            </div>
          </div>
        </div>
        <p :class="$style.eventTextContent" class="p1 color-primary-charcoal">{{ event.description }}</p>
        <div v-if="getAudienceSentence" :class="$style.audienceDetails">
          <MottoIcon name="UserGroup" icon-color="gray" />
          <p>{{ getAudienceSentence }}</p>
        </div>
        <div :class="$style.locationDetails">
          <MottoIcon name="Place" icon-color="gray" />
          <p class="p1 color-primary-charcoal">
            {{ event.meetingType }}
            <template v-if="event.location">- {{ event.location }}</template>
          </p>
        </div>
        <div v-for="url in event.urls" :key="url.url" :class="$style.urlDetails">
          <MottoIcon name="LinkChain" icon-color="gray" />
          <p @click="openEventUrl(url)">{{ url.url }}</p>
        </div>
        <div v-if="getEventPosts.length" :class="$style.supportingFiles">
          <div class="d-flex align-items-center">
            <MottoIcon name="Resources" icon-color="gray" />
            <p class="tag-text color-dark-gray">Supporting Files</p>
          </div>
          <div :class="$style.supportingFilesGrid">
            <ResourceBlock
              v-for="resource in getEventPosts"
              :key="resource.resourceId"
              :minimal-design="true"
              :class="$style.resourceBlock"
              :resource="resource" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.container {
  @media (max-width: $lg-breakpoint) {
    width: 100vw;
    min-width: 100vw;
  }

  display: flex;
  min-width: 600px;
  min-height: 100%;
}

.modalEventContainer {
  @media (max-width: $lg-breakpoint) {
    padding: 90px 30px 0 30px;
  }

  position: relative;
  display: flex;
  padding: 40px 80px 0 110px;
}

.breadcrumbs {
  display: flex;

  > svg {
    margin: 0 13px;
  }
}

.closeButton {
  position: absolute;
  top: 40px;
  left: 40px;
  cursor: pointer;
}

.contentContainer {
  @media (max-width: $lg-breakpoint) {
    width: 90%;
  }

  display: flex;
  flex-direction: column;
  width: 411px;
  padding-bottom: 72px;
}

.contentHeader {
  display: flex;
  align-items: flex-end;
}

.heartIcon {
  @media (max-width: $lg-breakpoint) {
    top: 30px;
  }

  position: absolute;
  top: 72px;
  right: 54px;
}

.eventTitleLink {
  text-decoration: none;
}

.eventTitle {
  @media (max-width: $lg-breakpoint) {
    margin-top: 30px;
  }

  margin-top: 72px;
  margin-right: 92px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.subTitleContent {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.subTitleEventDate {
  min-width: 175px;
}

.eventTextContent {
  margin-top: 32px !important;
  overflow-x: scroll;
  word-break: break-word;
  white-space: break-spaces;
}

.outlookButton {
  display: flex;
  align-items: center;
  margin-bottom: 8px;

  svg {
    margin-right: 9px;
  }

  p {
    font-weight: bold;
    color: $primary-blue;
    text-decoration: underline;
    cursor: pointer;
  }
}

.audienceDetails {
  display: flex;
  align-items: center;
  margin-bottom: 8px;

  svg {
    width: 18px;
    margin-right: 9px;
  }

  p {
    color: $darkest-gray;
  }
}

.locationDetails {
  display: flex;
  align-items: center;
  margin-bottom: 8px;

  svg {
    width: 18px;
    margin-right: 9px;
  }

  p {
    color: $darkest-gray;
    word-break: break-word;
    overflow-wrap: break-word;
  }
}

.urlDetails {
  display: flex;
  align-items: center;
  margin-bottom: 8px;

  svg {
    margin-right: 9px;
  }

  p {
    font-weight: bold;
    color: $primary-blue;
    text-decoration: underline;
    cursor: pointer;
  }
}

.supportingFiles {
  margin-top: 10px;

  svg:first-child {
    height: 18px;
    margin-right: 9px;
  }
}

.supportingFilesGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  grid-gap: 24px 2px;
  margin-top: 24px;
}

.resourceBlock {
  width: 130px;
}

.clipboardButton {
  @media (max-width: $lg-breakpoint) {
    top: 23px;
  }

  position: absolute;
  top: 65px;
  right: 0;
}
</style>
